import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent,
  CircularProgress, IconButton, Menu, MenuItem,
  FormControl, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tooltip,
  Chip, Divider, Tabs, Tab
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { normalizeColonyL } from '../utils/colonyLab';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Chart as ChartJS, LinearScale, PointElement, LineElement,
  Tooltip as ChartTooltip, Legend, Filler
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';

ChartJS.register(LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

// Metric definitions: key, display label, tooltip explanation, value range, format
const TEXTURE_METRICS = [
  {
    key: 'contrast',
    label: 'Contrast',
    tip: 'Average intensity difference between neighboring pixels. Higher = rougher, more varied surface texture.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'homogeneity',
    label: 'Homogeneity',
    tip: 'Similarity of neighboring pixel pairs (0-1). Higher = smoother, more uniform surface.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'energy',
    label: 'Energy',
    tip: 'Textural regularity (0-1). Higher = more repetitive or ordered texture pattern.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'correlation',
    label: 'Correlation',
    tip: 'Linear dependency between neighboring pixels (-1 to 1). Captures directional texture structure.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'edge_density',
    label: 'Edge Density',
    tip: 'Fraction of detected edges (0-1). Higher = more complex colony boundary and surface detail.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
];

const MORPHOLOGY_METRICS = [
  {
    key: 'area_pixels',
    label: 'Area (px)',
    tip: 'Total colony area in pixels. Reflects colony size at this time point.',
    fmt: (v) => v != null ? Math.round(v).toLocaleString() : '-',
  },
  {
    key: 'circularity',
    label: 'Circularity',
    tip: 'Shape compactness (0-1). 1.0 = perfect circle. Lower values indicate irregular or branching colonies.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'eccentricity',
    label: 'Eccentricity',
    tip: 'Colony elongation (0-1). 0 = circle, 1 = line segment. Higher = more elongated shape.',
    fmt: (v) => v?.toFixed(3) ?? '-',
  },
  {
    key: 'brightness_l',
    label: 'Brightness (L*)',
    tip: 'Lightness in CIE L*a*b* color space (0=black, 100=white). Indicates surface reflectance or pigmentation intensity.',
    fmt: (v) => v?.toFixed(1) ?? '-',
  },
];

const OBJECT_PALETTE = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#8BC34A', '#FF5722', '#00BCD4', '#E91E63',
];

function MetricHeader({ label, tip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
      {label}
      <Tooltip title={tip} placement="top" arrow>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: '#888', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

/**
 * Props:
 * - isOpen, onClose
 * - initialFrameIdx, maxFrameIdx
 * - objectMeta
 * - onApiCall(frameIdx) => Promise<{data:{objects:{[objId]: {contrast,..., frequency_log, radial_profile_log}}}}>
 * - cpuUrl, sessionId, s3Path  -- for phenotypic/measure endpoint
 * - availableFrames?: number[]
 */
const RugosityAnalysisChart = ({
  isOpen,
  onClose,
  initialFrameIdx = 0,
  maxFrameIdx = 0,
  objectMeta,
  onApiCall,
  cpuUrl,
  sessionId,
  s3Path,
  availableFrames,
}) => {
  const framesList = useMemo(() => {
    if (Array.isArray(availableFrames) && availableFrames.length) {
      return [...new Set(availableFrames)].map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    }
    return Array.from({ length: maxFrameIdx + 1 }, (_, i) => i);
  }, [availableFrames, maxFrameIdx]);

  const initialIndex = useMemo(() => {
    const idx = framesList.indexOf(initialFrameIdx);
    return idx >= 0 ? idx : 0;
  }, [framesList, initialFrameIdx]);

  const [cursorIdx, setCursorIdx] = useState(initialIndex);
  const [rows, setRows] = useState(null); // [{id, name, color, ...metrics, frequency_log, radial_profile_log}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0=spectrum, 1=metrics
  const chartRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setCursorIdx(initialIndex);
    void loadFrame(initialIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialIndex]);

  const realFrame = framesList[cursorIdx] ?? 0;

  const loadFrame = async (idx) => {
    const frame = framesList[Math.max(0, Math.min(idx, framesList.length - 1))];
    if (frame == null) return;
    setLoading(true);
    setError(null);
    setRows(null);

    try {
      // Fetch rugosity (GLCM + PSD) and phenotypic (morphology) in parallel
      const [rugResult, phenoResult] = await Promise.allSettled([
        onApiCall(frame),
        cpuUrl && sessionId
          ? fetch(`${cpuUrl}/phenotypic/measure`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId, frame_idx: frame, s3_path: s3Path || '' }),
            }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
          : Promise.resolve(null),
      ]);

      const rugObjects = rugResult.status === 'fulfilled' ? rugResult.value?.data?.objects : null;
      const phenoColonies = phenoResult.status === 'fulfilled' && phenoResult.value
        ? (phenoResult.value.colonies || [])
        : [];

      if (!rugObjects || !Object.keys(rugObjects).length) {
        setError(`No texture data found for frame ${frame}. Ensure masks have been tracked.`);
        return;
      }

      // Build a lookup map from phenotypic data by colony_id
      const phenoMap = {};
      phenoColonies.forEach((c) => {
        const colonyKey = String(c.colony_id);
        phenoMap[colonyKey] = {
          area_pixels: c.area_pixels,
          circularity: c.circularity,
          eccentricity: c.eccentricity,
          brightness_l: normalizeColonyL(c.mean_color_lab?.[0]),
        };
      });

      const merged = Object.entries(rugObjects).map(([objId, data], i) => ({
        id: objId,
        name: objectMeta?.[objId]?.name || `Colony ${objId}`,
        color: OBJECT_PALETTE[i % OBJECT_PALETTE.length],
        // Texture metrics
        contrast: data.contrast ?? null,
        homogeneity: data.homogeneity ?? null,
        energy: data.energy ?? null,
        correlation: data.correlation ?? null,
        edge_density: data.edge_density ?? null,
        // Power spectral density
        frequency_log: data.frequency_log || null,
        radial_profile_log: data.radial_profile_log || null,
        // Morphology metrics
        ...(phenoMap[objId] || { area_pixels: null, circularity: null, eccentricity: null, brightness_l: null }),
      }));

      setRows(merged);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    if (cursorIdx <= 0 || loading) return;
    const next = cursorIdx - 1;
    setCursorIdx(next);
    void loadFrame(next);
  };

  const handleNext = () => {
    if (cursorIdx >= framesList.length - 1 || loading) return;
    const next = cursorIdx + 1;
    setCursorIdx(next);
    void loadFrame(next);
  };

  const handleSelectChange = (e) => {
    const value = Number(e.target.value);
    const idx = framesList.indexOf(value);
    if (idx >= 0) {
      setCursorIdx(idx);
      void loadFrame(idx);
    }
  };

  // ── Download helpers ──

  const downloadMetricsXlsx = () => {
    if (!rows) return;
    const allMetrics = [...MORPHOLOGY_METRICS, ...TEXTURE_METRICS];
    const headers = ['Colony', ...allMetrics.map((m) => m.label)];
    const dataRows = rows.map((r) => [r.name, ...allMetrics.map((m) => {
      const raw = r[m.key];
      return raw != null ? raw : '';
    })]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers, ...dataRows]), 'Texture Metrics');
    XLSX.writeFile(wb, `texture_morphology_frame_${realFrame}.xlsx`);
  };

  const downloadSpectrumXlsx = () => {
    if (!rows) return;
    const maxLen = Math.max(...rows.filter((r) => r.frequency_log).map((r) => r.frequency_log.length), 0);
    if (!maxLen) return;
    const headers = ['log(Spatial Frequency)', ...rows.filter((r) => r.frequency_log).map((r) => `log(Power) - ${r.name}`)];
    const xlsxRows = [headers];
    for (let i = 0; i < maxLen; i++) {
      const row = [
        rows.find((r) => r.frequency_log)?.frequency_log[i] ?? '',
        ...rows.filter((r) => r.frequency_log).map((r) => r.radial_profile_log?.[i] ?? ''),
      ];
      xlsxRows.push(row);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(xlsxRows), 'Power Spectrum');
    XLSX.writeFile(wb, `power_spectrum_frame_${realFrame}.xlsx`);
  };

  const downloadChartPng = () => {
    const chart = chartRef.current;
    if (!chart?.toBase64Image) return;
    const url = chart.toBase64Image('image/png', 1.0);
    const a = document.createElement('a');
    a.href = url;
    a.download = `power_spectrum_frame_${realFrame}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Chart data ──

  const hasSpectrum = rows?.some((r) => r.frequency_log && r.frequency_log.length > 0);

  const spectrumChartData = useMemo(() => {
    if (!rows || !hasSpectrum) return null;
    return {
      datasets: rows
        .filter((r) => r.frequency_log && r.radial_profile_log)
        .map((r) => ({
          label: r.name,
          data: r.frequency_log.map((x, i) => ({ x, y: r.radial_profile_log[i] })),
          borderColor: r.color,
          backgroundColor: r.color + '33',
          pointRadius: 0,
          borderWidth: 2,
          showLine: true,
          tension: 0.3,
        })),
    };
  }, [rows, hasSpectrum]);

  const spectrumOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: freq=${ctx.parsed.x.toFixed(2)}, power=${ctx.parsed.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: 'log(Spatial Frequency)', font: { size: 13 } },
        grid: { color: '#e0e0e0' },
      },
      y: {
        type: 'linear',
        title: { display: true, text: 'log(Power)', font: { size: 13 } },
        grid: { color: '#e0e0e0' },
      },
    },
  }), []);

  const hasMorphology = rows?.some(
    (r) => r.area_pixels != null || r.circularity != null
  );

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Surface Texture Analysis</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Power spectral density + GLCM texture metrics per colony
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={(e) => setDownloadMenuAnchor(e.currentTarget)} title="Download">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small" title="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
        {/* Frame navigation */}
        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
          <IconButton onClick={handlePrev} disabled={cursorIdx <= 0 || loading} size="small">
            <NavigateBeforeIcon />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <TextField select label="Frame" value={realFrame} onChange={handleSelectChange} size="small">
              {framesList.map((f) => (
                <MenuItem key={f} value={f}>Frame {f}</MenuItem>
              ))}
            </TextField>
          </FormControl>
          <Typography variant="body2" sx={{ color: '#888' }}>
            {cursorIdx + 1} / {framesList.length}
          </Typography>
          <IconButton onClick={handleNext} disabled={cursorIdx >= framesList.length - 1 || loading} size="small">
            <NavigateNextIcon />
          </IconButton>
        </Box>

        {/* Content */}
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
            <CircularProgress size={48} />
            <Typography color="text.secondary">Loading data for frame {realFrame}...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : rows ? (
          <>
            {/* Tabs: Spectrum | Metrics */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
            >
              <Tab label="Power Spectrum" sx={{ minHeight: 36, textTransform: 'none' }} />
              <Tab label="Texture Metrics" sx={{ minHeight: 36, textTransform: 'none' }} />
            </Tabs>

            {/* Tab 0: Power Spectrum Chart */}
            {activeTab === 0 && (
              <Box sx={{ flex: 1, minHeight: 350 }}>
                {hasSpectrum && spectrumChartData ? (
                  <Scatter ref={chartRef} data={spectrumChartData} options={spectrumOptions} />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" flex={1} sx={{ minHeight: 200 }}>
                    <Typography color="text.secondary">
                      No power spectrum data available for this frame. Recompute analysis to generate spectral data.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Tab 1: Metrics Table */}
            {activeTab === 1 && (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ flex: '0 0 auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Colony</TableCell>
                        {hasMorphology && MORPHOLOGY_METRICS.map((m) => (
                          <TableCell key={m.key} align="right" sx={{ fontWeight: 700, minWidth: 90 }}>
                            <MetricHeader label={m.label} tip={m.tip} />
                          </TableCell>
                        ))}
                        <TableCell sx={{ bgcolor: '#f5f5f5' }}><Divider orientation="vertical" flexItem /></TableCell>
                        {TEXTURE_METRICS.map((m) => (
                          <TableCell key={m.key} align="right" sx={{ fontWeight: 700, minWidth: 90 }}>
                            <MetricHeader label={m.label} tip={m.tip} />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
                              <Typography variant="body2">{row.name}</Typography>
                            </Box>
                          </TableCell>
                          {hasMorphology && MORPHOLOGY_METRICS.map((m) => (
                            <TableCell key={m.key} align="right">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {m.fmt(row[m.key])}
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell />
                          {TEXTURE_METRICS.map((m) => (
                            <TableCell key={m.key} align="right">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {m.fmt(row[m.key])}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Metric guide */}
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#444' }}>
                    How to read these metrics
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
                    {[...(hasMorphology ? MORPHOLOGY_METRICS : []), ...TEXTURE_METRICS].map((m) => (
                      <Box key={m.key}>
                        <Chip label={m.label} size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }} />
                        <Typography variant="caption" sx={{ display: 'block', color: '#666', lineHeight: 1.5 }}>
                          {m.tip}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </>
        ) : null}
      </DialogContent>

      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => { downloadMetricsXlsx(); setDownloadMenuAnchor(null); }}>
          Download Metrics (XLSX)
        </MenuItem>
        {hasSpectrum && (
          <MenuItem onClick={() => { downloadSpectrumXlsx(); setDownloadMenuAnchor(null); }}>
            Download Spectrum Data (XLSX)
          </MenuItem>
        )}
        {hasSpectrum && (
          <MenuItem onClick={() => { downloadChartPng(); setDownloadMenuAnchor(null); }}>
            Download Spectrum Chart (PNG)
          </MenuItem>
        )}
      </Menu>
    </Dialog>
  );
};

export { RugosityAnalysisChart };
export default RugosityAnalysisChart;
